/**
 * "Ask a grown-up to update" — ipad-native-shell R15.1 / task 15.2.
 *
 * An installed iPad app runs the JavaScript it shipped with while the backend
 * moves on every push to `main`. When the backend says this build is below
 * `min_ios_version`, show one warm panel. Builds at or above it never see it.
 *
 * Below the minimum it cannot be dismissed (operator, 2026-11-02, "versions:
 * block"), but only once a store URL is configured. A panel with no dismiss
 * and no Update button would leave the child nowhere to go, so with no store
 * URL it stays the old nudge: "Not now" closes it, and it comes back the next
 * time the app returns to the foreground while the build is still too old. A
 * policy that cannot be read (offline, route down) never opens it.
 *
 * The store link is an outside link, so it sits behind the Parental_Gate
 * (`link` reason). A grown-up who updates returns the app to the foreground,
 * which checks again and closes the panel once the build is supported.
 *
 * Mounted once beside <App /> in main.tsx so it covers every route, signed in
 * or not. Off-native it renders nothing and never fetches.
 */
import { useCallback, useEffect, useState } from "react";
import { getClientVersion, isNativeShell, request } from "../api";
import { PanelSurface } from "../chrome/PanelSurface";
import { PillButton } from "../chrome/PillButton";
import { ChromeTokens } from "../chrome/tokens";
import { useParentalGate } from "../safety/ParentalGate";

export interface ClientPolicy {
  min_ios_version: string;
  latest_ios_version: string;
  store_url: string;
  update_required?: boolean;
}

/** `"1.10.0"` vs `"1.9"` compared numerically, the way people mean it. */
export function isBelow(version: string, minimum: string): boolean {
  const a = version.split(".").map((n) => parseInt(n, 10) || 0);
  const b = minimum.split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d < 0;
  }
  return false;
}

/** True when this build should be asked to update. Trusts the server's answer
 *  when it gave one, and compares itself when the header never reached it. */
export function updateRequired(policy: ClientPolicy, installed: string): boolean {
  if (policy.update_required) return true;
  return installed !== "" && isBelow(installed, policy.min_ios_version);
}

export function NativeUpdateHost(): JSX.Element | null {
  const native = isNativeShell();
  const [policy, setPolicy] = useState<ClientPolicy | null>(null);
  const [open, setOpen] = useState(false);
  const gate = useParentalGate();

  const check = useCallback(async () => {
    try {
      const p = await request<ClientPolicy>("/meta/client-policy");
      setPolicy(p);
      // Closes as well as opens, so a block lifts once the build is supported.
      setOpen(updateRequired(p, getClientVersion()));
    } catch {
      // Offline or the route is down: never nag on a guess.
    }
  }, []);

  useEffect(() => {
    if (!native) return;
    void check();
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [native, check]);

  if (!native) return null;

  const storeUrl = policy?.store_url ?? "";
  // The panel only opens below the minimum, so open + a store URL is the block.
  const blocking = open && storeUrl !== "";
  const dismiss = () => {
    if (!blocking) setOpen(false);
  };
  const openStore = () =>
    gate.run("link", () => {
      // Capacitor hands a `_blank` https link to the system, which opens the
      // App Store app for an apps.apple.com URL.
      window.open(storeUrl, "_blank");
    });

  return (
    <>
      <PanelSurface
        open={open}
        onDismiss={dismiss}
        dismissOnOutsideClick={!blocking}
        ariaLabel="A new version is ready"
      >
        <div style={{ maxWidth: 360, display: "flex", flexDirection: "column", gap: ChromeTokens.spacing.md, color: ChromeTokens.color.ink }}>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>A new version is ready!</p>
          <p style={{ margin: 0, fontSize: 15, color: ChromeTokens.color.inkMuted }}>
            Ask a grown-up to update world by me so everything keeps working.
          </p>
          <div style={{ display: "flex", gap: ChromeTokens.spacing.sm, justifyContent: "flex-end" }}>
            {!blocking && (
              <PillButton size="md" tone="subtle" ariaLabel="Not now" onClick={dismiss}>
                Not now
              </PillButton>
            )}
            {storeUrl && (
              <PillButton size="md" tone="primary" ariaLabel="Update" onClick={openStore}>
                Update
              </PillButton>
            )}
          </div>
        </div>
      </PanelSurface>
      {gate.Gate && <gate.Gate />}
    </>
  );
}
